import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Button, Form, Spinner, InputGroup, FormControl, ButtonGroup, ProgressBar, Modal } from 'react-bootstrap';
import { FaShieldAlt, FaArrowUp, FaArrowDown, FaSync, FaChartLine, FaSearch, FaTh, FaList, FaChevronRight, FaPlus, FaTrash } from 'react-icons/fa';
import Select from 'react-select';
import Swal from 'sweetalert2';
import ReportDetailView from './ReportDetailView';

const API_BASE = 'https://hiyg5j9ysa.execute-api.us-west-2.amazonaws.com/prod';
const API_V3_BASE = 'https://zlbo8rtfd0.execute-api.us-west-2.amazonaws.com/dev/v3';

function WellArchitectedNew() {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingText, setLoadingText] = useState('Loading Well-Architected...');

  // Support both tenant and company users
  const TENANT_ID = localStorage.getItem('tenantID');
  const COMPANY_ID = localStorage.getItem('companyID');
  const USER_TYPE = localStorage.getItem('userType');
  const IS_COMPANY_USER = USER_TYPE === 'company' && COMPANY_ID;

  useEffect(() => {
    loadReports();
  }, []);
  
  useEffect(() => {
    // Poll for in-progress reports every 10 seconds
    const hasInProgress = reports.some(r => 
      r.status === 'pending' || 
      r.status === 'IN_PROGRESS' || 
      r.status === 'analyzing' || 
      r.status === 'data_collected'
    );
    
    if (!hasInProgress) return;
    
    const pollInterval = setInterval(() => {
      loadReports();
    }, 10000);
    
    return () => clearInterval(pollInterval);
  }, [reports.length]); // Only depend on length, not full array

  const getConnectedAccounts = async () => {
    try {
      let response;
      
      if (IS_COMPANY_USER) {
        // Company user - get company accounts
        response = await fetch(`https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/company/accounts?companyID=${COMPANY_ID}`);
        
        if (!response.ok) throw new Error("Failed to fetch company accounts");
        
        const data = await response.json();
        return data.accounts.map(acc => ({
          awsAccountId: acc.accountId,
          alias: acc.accountName
        }));
      } else {
        // Tenant user - get tenant accounts
        response = await fetch(`https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/tenant/settings?tenantID=${TENANT_ID}`);

        if (!response.ok) throw new Error("Failed to fetch accounts");

        const data = await response.json();
        const accounts = [];
        
        // Add standalone AWS accounts
        data.cloudAccounts?.aws?.forEach(acc => {
          accounts.push({ awsAccountId: acc.accountId, alias: acc.alias });
        });
        
        // Add organization member accounts
        data.cloudAccounts?.organizations?.forEach(org => {
          org.memberAccounts?.forEach(acc => accounts.push({ awsAccountId: acc.accountId, alias: acc.accountName }));
        });
        
        return accounts;
      }
    } catch (error) {
      console.error('Error getting connected accounts:', error);
    }
    return [];
  };

  const loadReports = async () => {
    try {
      const accounts = await getConnectedAccounts();
      
      if (accounts.length === 0) {
        setReports([]);
        setLoading(false);
        return;
      }

      setLoading(false);
      
      // Fetch all accounts in parallel
      const allReportsPromise = Promise.all(
        accounts.map(async (account) => {
          try {
            // Use appropriate tenant ID for API calls
            const apiTenantId = IS_COMPANY_USER ? account.tenantID || TENANT_ID : TENANT_ID;
            // v3 API: Use query params instead of path params
            const response = await fetch(`${API_V3_BASE}/review?tenantId=${apiTenantId}&accountId=${account.awsAccountId}`);
            if (response.ok) {
              const data = await response.json();
              return data.reports
                .filter(r => !r.reportId.startsWith('test'))
                .map(r => ({
                  ...r,
                  accountId: account.awsAccountId,
                  accountAlias: account.alias,
                  // Map v3 status to v2 format for backward compatibility
                  status: r.status === 'IN_PROGRESS' ? 'pending' : 
                          r.status === 'COMPLETED' ? 'completed' : 
                          r.status === 'FAILED' ? 'failed' : 
                          r.status.toLowerCase(),
                  progress: r.status === 'COMPLETED' ? 100 : 
                           (r.progress?.completedPillars && Array.isArray(r.progress.completedPillars)) ? 
                           Math.round((r.progress.completedPillars.length / 6) * 100) : 0,
                  // Ensure overallScore is null instead of undefined for in-progress reports
                  overallScore: r.overallScore !== undefined ? r.overallScore : null,
                  // Pass through currentPillar for status display
                  currentPillar: r.progress?.currentPillar
                }));
            }
          } catch (err) {
            console.error(`Failed to fetch reports for account ${account.awsAccountId}:`, err);
          }
          return [];
        })
      );
      
      const allReportsArrays = await allReportsPromise;
      const allReports = allReportsArrays.flat();
      allReports.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setReports(allReports);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const loadReportDetail = async (reportId, accountId) => {
    try {
      setLoading(true);
      setError(null);
      console.log('[WAF] Loading report detail:', { reportId, accountId });
      
      // Find the account alias from the reports list
      const reportInList = reports.find(r => r.reportId === reportId && r.accountId === accountId);
      const accountAlias = reportInList?.accountAlias || accountId;
      
      // Use appropriate tenant ID for API calls
      const apiTenantId = IS_COMPANY_USER ? TENANT_ID : TENANT_ID; // For company users, we still use their tenantID for WAF API
      // v3 API: Use query params instead of path params
      const url = `${API_V3_BASE}/review/${reportId}?tenantId=${apiTenantId}&accountId=${accountId}`;
      console.log('[WAF] Fetching from:', url);
      
      const response = await fetch(url);
      console.log('[WAF] Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`Failed to load report: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('[WAF] Report data received:', {
        hasAggregatedReport: !!data.aggregatedReport,
        hasExecutiveSnapshot: !!data.aggregatedReport?.executiveSnapshot,
        hasPillars: !!data.aggregatedReport?.pillars,
        pillarCount: Object.keys(data.aggregatedReport?.pillars || {}).length,
        overallScore: data.aggregatedReport?.executiveSnapshot?.overallScore
      });
      
      // Check if data has the expected structure
      if (!data.aggregatedReport) {
        console.error('[WAF] Report missing aggregatedReport structure:', data);
        throw new Error('Report data is incomplete or in unexpected format');
      }
      
      // Add accountAlias to the report data
      data.accountAlias = accountAlias;
      
      setSelectedReport(data);
      console.log('[WAF] Report loaded successfully');
    } catch (err) {
      console.error('[WAF] Error loading report detail:', err);
      setError(err.message);
      setSelectedReport(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId, accountId, e) => {
    e.stopPropagation();
    
    const result = await Swal.fire({
      title: 'Delete Report?',
      html: `<p>Are you sure you want to delete this report?</p><code style="font-size: 12px; color: #666;">${reportId}</code>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const apiTenantId = IS_COMPANY_USER ? TENANT_ID : TENANT_ID;
        // v3 API: Use query params instead of path params
        const response = await fetch(`${API_V3_BASE}/review/${reportId}?tenantId=${apiTenantId}&accountId=${accountId}`, {
          method: 'DELETE'
        });
        
        if (!response.ok) throw new Error('Failed to delete report');
        
        setReports(prev => prev.filter(r => r.reportId !== reportId));
        
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Report has been deleted successfully.',
          confirmButtonColor: '#342ead'
        });
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.message || 'Failed to delete report',
          confirmButtonColor: '#342ead'
        });
      }
    }
  };

  // Rotating loading messages for better UX
  useEffect(() => {
    if (loading && !selectedReport) {
      const messages = [
        'Loading Well-Architected...',
        'Fetching framework reports...',
        'Analyzing workload reviews...',
        'Retrieving assessment data...',
        'Preparing your framework dashboard...'
      ];
      let index = 0;
      const interval = setInterval(() => {
        index = (index + 1) % messages.length;
        setLoadingText(messages[index]);
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [loading, selectedReport]);

  if (loading && !selectedReport) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)', paddingTop: '80px' }}>
        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem' }}>{loadingText}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-5">
        <p className="text-danger">{error}</p>
        <Button onClick={loadReports} style={{ backgroundColor: '#342ead', border: 'none' }}>Retry</Button>
      </div>
    );
  }

  if (selectedReport) {
    return <ReportDetailView report={selectedReport} onBack={() => setSelectedReport(null)} />;
  }

  return <ReportsListView reports={reports} onSelect={loadReportDetail} onRefresh={loadReports} onDelete={handleDeleteReport} />;
}

function ReportsListView({ reports, onSelect, onRefresh, onDelete }) {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [cloudAccounts, setCloudAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Support both tenant and company users
  const TENANT_ID = localStorage.getItem('tenantID');
  const COMPANY_ID = localStorage.getItem('companyID');
  const USER_TYPE = localStorage.getItem('userType');
  const IS_COMPANY_USER = USER_TYPE === 'company' && COMPANY_ID;

  useEffect(() => {
    fetchCloudAccounts();
  }, []);

  const fetchCloudAccounts = async () => {
    try {
      let response;
      
      if (IS_COMPANY_USER) {
        // Company user - get company accounts
        response = await fetch(`https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/company/accounts?companyID=${COMPANY_ID}`);
        
        if (!response.ok) throw new Error("Failed to fetch company accounts");
        
        const data = await response.json();
        setCloudAccounts(data.accounts.map(acc => ({
          awsAccountId: acc.accountId,
          alias: acc.accountName
        })));
      } else {
        // Tenant user - get tenant accounts
        response = await fetch(`https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/tenant/settings?tenantID=${TENANT_ID}`);

        if (!response.ok) throw new Error("Failed to fetch accounts");

        const data = await response.json();
        const accounts = [];
        
        // Add standalone AWS accounts
        data.cloudAccounts?.aws?.forEach(acc => {
          accounts.push({ awsAccountId: acc.accountId, alias: acc.alias });
        });
        
        // Add organization member accounts
        data.cloudAccounts?.organizations?.forEach(org => {
          org.memberAccounts?.forEach(acc => accounts.push({ awsAccountId: acc.accountId, alias: acc.accountName }));
        });
        
        setCloudAccounts(accounts);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const handleCreateReport = async () => {
    if (!selectedAccount) {
      Swal.fire({
        icon: 'warning',
        title: 'Account Required',
        text: 'Please select an AWS account'
      });
      return;
    }

    setIsCreating(true);
    try {
      const apiTenantId = IS_COMPANY_USER ? TENANT_ID : TENANT_ID;
      // v3 API: Use /v3/review/trigger with mode parameter
      const response = await fetch(`${API_V3_BASE}/review/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          tenantId: apiTenantId,  // Changed from organizationId
          accountId: selectedAccount.value,
          mode: 'FULL_AUDIT'  // NEW: Default to FULL_AUDIT mode
        })
      });
      
      if (!response.ok) throw new Error('Failed to create report');
      
      const data = await response.json();
      
      // Add new report card immediately with IN_PROGRESS status
      const newReport = {
        reportId: data.reportId,
        accountId: selectedAccount.value,
        accountAlias: selectedAccount.alias || selectedAccount.label,
        status: 'pending',  // Map IN_PROGRESS to pending for UI
        progress: 0,
        createdAt: data.createdAt || new Date().toISOString(),
        overallScore: null,
        maturityLevel: null
      };
      
      onRefresh(); // Add new report to list
      
      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Well-Architected Review initiated successfully!',
        timer: 2000,
        showConfirmButton: false
      });
      
      setShowModal(false);
      setSelectedAccount(null);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to create report'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const filtered = reports.filter(r => 
    r.reportId.toLowerCase().includes(search.toLowerCase()) ||
    new Date(r.createdAt).toLocaleDateString().includes(search)
  );

  const avgScore = Math.round(reports.reduce((sum, r) => sum + (r.overallScore || 0), 0) / reports.length) || 0;
  const criticalScoreCount = reports.filter(r => r.overallScore && r.overallScore < 40).length;

  const getScoreColor = (score) => {
    if (!score) return '#6c757d';        // Gray - No score
    if (score >= 90) return '#28a745';   // Green - Optimizing
    if (score >= 75) return '#17a2b8';   // Cyan - Quantitatively Managed
    if (score >= 60) return '#342ead';   // Purple-Blue - Defined
    if (score >= 40) return '#ffc107';   // Yellow - Managed
    return '#dc3545';                     // Red - Initial
  };

  const getStatusBadge = (status, currentPillar) => {
    // Generate descriptive status based on current pillar
    if (status === 'pending' || status === 'IN_PROGRESS') {
      const pillarMessages = {
        'supervisor': 'Initializing Assessment',
        'security': 'Analyzing Security',
        'cost': 'Analyzing Cost Optimization',
        'reliability': 'Analyzing Reliability',
        'performance': 'Analyzing Performance',
        'operational': 'Analyzing Operations',
        'sustainability': 'Analyzing Sustainability',
        'aggregator': 'Generating Insights'
      };
      const message = pillarMessages[currentPillar] || 'In Progress';
      return { color: '#007bff', text: message };
    }
    
    const statusMap = {
      // v2 status values (for backward compatibility)
      'completed': { color: '#28a745', text: 'Completed' },
      'analysis_complete': { color: '#17a2b8', text: 'Analysis Complete' },
      'analyzing': { color: '#007bff', text: 'Analyzing' },
      'data_collected': { color: '#ffc107', text: 'Data Collected' },
      'failed': { color: '#dc3545', text: 'Failed' },
      // v3 status values (mapped from uppercase)
      'COMPLETED': { color: '#28a745', text: 'Completed' },
      'FAILED': { color: '#dc3545', text: 'Failed' },
      'PARTIAL_SUCCESS': { color: '#ffc107', text: 'Partial Success' },
      'CANCELLED': { color: '#6c757d', text: 'Cancelled' }
    };
    return statusMap[status] || { color: '#6c757d', text: status };
  };

  return (
    <div className="container-fluid pb-5" style={{ paddingTop: '80px', paddingLeft: '15px', paddingRight: '15px' }}>
      <div className="container" style={{ maxWidth: '100%', paddingLeft: '15px', paddingRight: '15px' }}>
        <div className="d-flex justify-content-between align-items-center mb-4" style={{ marginTop: '20px' }}>
          <h2 style={{ fontWeight: '600', color: '#1e293b', marginBottom: 0 }}>
            Well-Architected Review Reports
            {IS_COMPANY_USER && (
              <small className="text-muted ms-2">({localStorage.getItem('companyName') || 'Company View'})</small>
            )}
          </h2>
          <div className="d-flex gap-2">
            <Button onClick={onRefresh} variant="outline-secondary" size="sm">
              <FaSync className="me-2" /> Refresh
            </Button>
            <Button 
              onClick={() => setShowModal(true)}
              size="sm"
              style={{
                backgroundColor: '#6D5BFF',
                color: 'white',
                fontWeight: '600',
                border: 'none'
              }}
            >
              <FaPlus className="me-2" /> Create Report
            </Button>
          </div>
        </div>

        <Row className="mb-4">
          <Col md={3}>
            <Card style={{ 
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
            }}>
              <Card.Body>
                <small className="text-muted">Total Reports</small>
                <h3 className="mb-0">{reports.length}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ 
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
            }}>
              <Card.Body>
                <small className="text-muted">Average Score</small>
                <h3 className="mb-0" style={{ color: getScoreColor(avgScore) }}>{avgScore}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ 
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
            }}>
              <Card.Body>
                <small className="text-muted">Critical Scores (&lt;40)</small>
                <h3 className="mb-0 text-danger">{criticalScoreCount}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="d-flex gap-3 mb-4">
          <InputGroup style={{ flex: 1 }}>
            <InputGroup.Text style={{ backgroundColor: 'white' }}><FaSearch /></InputGroup.Text>
            <FormControl 
              placeholder="Search by report ID or date..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <ButtonGroup>
            <Button 
              variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
              onClick={() => setViewMode('grid')}
              style={viewMode === 'grid' ? { backgroundColor: '#342ead', border: 'none' } : {}}
            >
              <FaTh />
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
              onClick={() => setViewMode('list')}
              style={viewMode === 'list' ? { backgroundColor: '#342ead', border: 'none' } : {}}
            >
              <FaList />
            </Button>
          </ButtonGroup>
        </div>
        <div className="mb-3 text-muted small">Showing {filtered.length} of {reports.length} reports</div>

        {viewMode === 'grid' ? (
          <Row>
            {filtered.map(report => (
              <Col md={4} key={report.reportId} className="mb-4">
                <Card 
                  style={{ 
                    cursor: 'pointer', 
                    transition: 'all 0.3s', 
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    height: '100%'
                  }}
                  onClick={() => onSelect(report.reportId, report.accountId)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#342ead';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(52,42,173,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#dee2e6';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <div className="text-muted small">{report.accountAlias || 'N/A'}</div>
                        <code className="text-muted" style={{ fontSize: '11px' }}>{report.accountId}</code>
                      </div>
                      <div className="d-flex flex-column align-items-end gap-1">
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-danger"
                            onClick={(e) => onDelete(report.reportId, report.accountId, e)}
                            style={{ opacity: 0.7, transition: 'opacity 0.2s' }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
                          >
                            <FaTrash size={14} />
                          </Button>
                          <span style={{ 
                            fontSize: '8px', 
                            padding: '3px 6px', 
                            borderRadius: '4px', 
                            backgroundColor: getStatusBadge(report.status, report.currentPillar).color,
                            color: 'white',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}>
                            {getStatusBadge(report.status, report.currentPillar).text}
                          </span>
                        </div>
                        <FaChevronRight className="text-muted" style={{ opacity: 0.5 }} />
                      </div>
                    </div>
                    {report.status !== 'completed' && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted">Progress</small>
                          <small className="text-muted">{report.progress}%</small>
                        </div>
                        <ProgressBar now={report.progress} style={{ height: '6px' }} variant="primary" />
                      </div>
                    )}

                    {report.status === 'completed' ? (
                      <>
                        <div className="text-center my-4">
                          <div 
                            className="d-inline-flex align-items-center justify-content-center rounded-circle position-relative"
                            style={{
                              width: '100px',
                              height: '100px',
                              border: `4px solid ${getScoreColor(report.overallScore)}`,
                              backgroundColor: '#f8f9fa'
                            }}
                          >
                            <div>
                              <h2 style={{ color: getScoreColor(report.overallScore), marginBottom: 0 }}>
                                {report.overallScore !== null && report.overallScore !== undefined ? report.overallScore : '-'}
                              </h2>
                              <small className="text-muted">Score</small>
                            </div>
                            {report.scoreDelta !== 0 && (
                              <div 
                                className="position-absolute"
                                style={{ top: '-8px', right: '-8px', backgroundColor: 'white', borderRadius: '50%', padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                              >
                                <small style={{ color: report.scoreDelta > 0 ? '#28a745' : '#dc3545', fontSize: '11px' }}>
                                  {report.scoreDelta > 0 ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(report.scoreDelta)}
                                </small>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Maturity Level</small>
                          <Badge bg="secondary" style={{ fontSize: '11px', fontWeight: 600 }}>{report.maturityLevel || 'N/A'}</Badge>
                        </div>
                      </>
                    ) : (
                      <div className="text-center my-4 py-3">
                        <Spinner animation="border" variant="primary" size="sm" className="mb-2" />
                        <div className="text-muted small">Assessment in progress...</div>
                      </div>
                    )}

                    <div className="text-muted small pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                      <div>Report ID: <code style={{ fontSize: '10px', color: '#666' }}>{report.reportId}</code></div>
                      <div className="mt-1">Created: {new Date(report.createdAt.split('T')[0]).toLocaleDateString()}</div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div>
            {filtered.map(report => (
              <Card 
                key={report.reportId}
                className="mb-3"
                style={{ 
                  cursor: 'pointer', 
                  transition: 'all 0.3s', 
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}
                onClick={() => onSelect(report.reportId, report.accountId)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#342ead';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(52,42,173,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#dee2e6';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                }}
              >
                <Card.Body>
                  <div className="d-flex align-items-center gap-3">
                    {report.status === 'completed' ? (
                      <div 
                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{
                          width: '60px',
                          height: '60px',
                          border: `3px solid ${getScoreColor(report.overallScore)}`,
                          backgroundColor: '#f8f9fa'
                        }}
                      >
                        <h5 style={{ color: getScoreColor(report.overallScore), marginBottom: 0 }}>
                          {report.overallScore !== null && report.overallScore !== undefined ? report.overallScore : '-'}
                        </h5>
                      </div>
                    ) : (
                      <div 
                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{
                          width: '60px',
                          height: '60px',
                          border: '3px solid #007bff',
                          backgroundColor: '#f8f9fa'
                        }}
                      >
                        <Spinner animation="border" variant="primary" size="sm" />
                      </div>
                    )}
                    
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="text-muted small">{report.accountAlias || 'N/A'}</span>
                        <code className="text-muted" style={{ fontSize: '10px' }}>{report.accountId}</code>
                      </div>
                      <div className="d-flex gap-4 small text-muted">
                        <div>
                          <span>Report ID: </span>
                          <code style={{ fontSize: '9px', color: '#666' }}>{report.reportId}</code>
                        </div>
                        {report.status === 'completed' && (
                          <>
                            <div>
                              <span>Maturity: </span>
                              <Badge bg="secondary" style={{ fontSize: '9px', fontWeight: 600 }}>{report.maturityLevel || 'N/A'}</Badge>
                            </div>
                          </>
                        )}
                        <div>
                          <span>Created: </span>
                          <strong className="text-dark">{report.createdAt ? new Date(report.createdAt.split('T')[0]).toLocaleDateString() : 'N/A'}</strong>
                        </div>
                        {report.status !== 'completed' && (
                          <div>
                            <span>Progress: </span>
                            <strong className="text-dark">{report.progress}%</strong>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        onClick={(e) => onDelete(report.reportId, report.accountId, e)}
                        style={{ opacity: 0.7, transition: 'opacity 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
                      >
                        <FaTrash size={16} />
                      </Button>
                      <span style={{ 
                        fontSize: '8px', 
                        padding: '3px 6px', 
                        borderRadius: '4px', 
                        backgroundColor: getStatusBadge(report.status, report.currentPillar).color,
                        color: 'white',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px'
                      }}>
                        {getStatusBadge(report.status, report.currentPillar).text}
                      </span>
                      {report.scoreDelta !== 0 && (
                        <Badge bg={report.scoreDelta > 0 ? 'success' : 'danger'} style={{ fontSize: '10px' }}>
                          {report.scoreDelta > 0 ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(report.scoreDelta)}
                        </Badge>
                      )}
                      <FaChevronRight className="text-muted" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <Card className="text-center py-5">
            <Card.Body>
              <FaSearch className="text-muted mb-3" style={{ fontSize: '48px', opacity: 0.3 }} />
              <h5 className="text-muted">No reports found</h5>
              <p className="text-muted small">Try adjusting your search criteria</p>
            </Card.Body>
          </Card>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa' }}>
          <Modal.Title>
            Create New Well-Architected Review
            {IS_COMPANY_USER && (
              <small className="text-muted d-block" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                Company: {localStorage.getItem('companyName') || 'Unknown'}
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <div className="mb-3">
            <Form.Label className="text-muted small">Cloud Provider</Form.Label>
            <Select
              className="react-select"
              classNamePrefix="react-select"
              options={[{ value: "AWS", label: "AWS" }]}
              value={{ value: "AWS", label: "AWS" }}
              isDisabled
            />
            <Form.Text className="text-muted">Currently only AWS is supported</Form.Text>
          </div>
          <div className="mb-3">
            <Form.Label className="text-muted small">Select Account</Form.Label>
            <Select
              className="react-select"
              classNamePrefix="react-select"
              options={cloudAccounts.map(account => ({
                value: account.awsAccountId,
                label: (account.alias || account.description) ? 
                  `${account.alias || account.description} (${account.awsAccountId})` : 
                  account.awsAccountId,
                alias: account.alias || account.description
              }))}
              value={selectedAccount}
              onChange={(selected) => setSelectedAccount(selected)}
              placeholder="Select Account"
            />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f8f9fa' }}>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateReport}
            disabled={isCreating}
            style={{
              backgroundColor: '#6D5BFF',
              color: 'white',
              fontWeight: '600',
              border: 'none'
            }}
          >
            {isCreating ? 'Creating...' : 'Create Report'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default WellArchitectedNew;
